import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const filteredProjects = selectedCategory === 'All' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Our Projects - Construction Portfolio Ipswich Suffolk"
        description="View our completed construction projects in Ipswich, Suffolk. Kitchen renovations, bathroom installations, house extensions, painting projects. See our quality workmanship."
        keywords="construction projects Ipswich, renovation portfolio Suffolk, completed building work, kitchen renovation examples, bathroom installation gallery, house extension projects"
        canonical="/projects"
      />
      <section className="pt-56 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">Our Projects</h1>
            <p className="text-xl text-gray-600">
              Explore our portfolio of successfully completed construction and renovation projects
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-gray-600 text-xl">Loading projects...</p>
            </div>
          ) : (
            <>
              <div className="flex justify-center mb-12">
                <button 
                  onClick={() => setSelectedCategory('All')}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                    selectedCategory === 'All' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Projects
                </button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProjects.map((project) => (
                  <Link key={project.id} to={`/projects/${project.id}`}>
                    <Card className="overflow-hidden group cursor-pointer hover:shadow-2xl transition-all duration-300">
                      <div className="relative h-64 overflow-hidden">
                        <img 
                          src={project.image} 
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <Badge className="absolute top-4 right-4 bg-orange-600 text-white">
                          {project.category}
                        </Badge>
                      </div>
                      <CardHeader>
                        <CardTitle className="group-hover:text-orange-600 transition-colors">{project.title}</CardTitle>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <LucideIcons.MapPin className="w-4 h-4" />
                          <span>{project.location}</span>
                        </div>
                        <CardDescription>{project.description}</CardDescription>
                      </CardHeader>
                    </Card>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;